import AutoCompleteRet from "../autocomplete/AutoCompleteRet";
import { useEffect, useState } from "react";
import { AutoCompleteItem } from "../autocomplete/interface";
import { FormikProps } from "formik";
import ValidationErrorMessage from "./ValidationErrorMessage";
import IsRequired from "./common/IsRequired";
import FormControlButtonSmall from "../button/FormControlButtonSmall";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../api/util";
import { FirmaDto } from "../../interfaces/schema";

const RetFormControl = <U,>({
  config,
  formik,
  disabled,
  value,
}: FormikRetControlProps<U>) => {
  const { setFieldValue, setFieldTouched, touched, errors } =
    formik as FormikProps<any>;
  const { id, label, isRequired, addressSetterId } = config;
  const [selectedItem, setSelectedItem] = useState<AutoCompleteItem<any>>({
    id: "",
    header: "",
  });
  const { data, isLoading } = useQuery({
    queryKey: ["retter"],
    queryFn: async () =>
      get<FirmaDto[]>({
        query: "retter",
      }),
    enabled: !!value,
  });
  useEffect(() => {
    if (value && !isLoading) {
      setSelectedItem({
        id: value ?? "",
        header:
          value && data ? (data.find((e) => e.id === value)?.navn1 ?? "") : "",
      });
    }
  }, [value, isLoading, data]);
  const remove = () => {
    setShouldCaptureFocusOnChange(true);
    setFieldTouched(id, true);
    setFieldValue(id, undefined);
  };
  const [shouldCaptureFocusOnChange, setShouldCaptureFocusOnChange] =
    useState(false);
  return (
    <>
      <label htmlFor={id} className="form-label">
        {label}
        {isRequired && <IsRequired />}
      </label>
      {value && (
        <FormControlButtonSmall
          disabled={disabled}
          selectedItem={selectedItem}
          remove={remove}
          focusOnMount={shouldCaptureFocusOnChange}
        />
      )}
      {!value && (
        <AutoCompleteRet
          onBlur={() => setFieldTouched(id, true)}
          onSelectedItemEvent={async (item) => {
            setShouldCaptureFocusOnChange(true);
            const {
              id: itemId,
              navn1,
              beliggenhedsadresse: address,
            } = item.selectedItem || {};

            await setFieldValue(id, itemId, true);
            setSelectedItem({
              id: itemId ?? "",
              header: navn1 ?? "",
            });
            await setFieldTouched(id, true);

            if (addressSetterId && address) {
              const formattedAddress = address.toLines
                ? address.toLines.join(", ")
                : `${address.adresselinie1}, ${address.postnummer} ${address.bynavn}, ${address.landekode}`;

              await setFieldValue(addressSetterId, formattedAddress, true);
              await setFieldTouched(addressSetterId, true);
            }

            await formik.validateForm();
          }}
          focusInputOnMount={shouldCaptureFocusOnChange}
        />
      )}
      <ValidationErrorMessage
        touched={touched[id]}
        errorMessage={(errors as any)[id]}
      />
    </>
  );
};

export default RetFormControl;
